/**
 * Criar conta — cadastro público.
 *
 * Conta e participação em viagem são coisas diferentes: esta tela cria a conta
 * do aplicativo, e mais nada. Depois dela, a pessoa cria uma viagem ou entra
 * numa por convite.
 *
 * Quando se chega aqui **a partir de um convite** (`/criar-conta?token=…`), o
 * convite aparece como contexto e o aceite continua logo após o cadastro — mas
 * o convite não é credencial de cadastro: se ele tiver expirado no meio do
 * caminho, a conta continua existindo, e a pessoa vê o que houve em vez de
 * perder o cadastro que acabou de fazer.
 */
import { normalizarNome } from '@nv/core';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  acceptInvitationAsMe,
  ApiError,
  fetchMe,
  previewInvitation,
  signUp,
  type InvitationPreview,
} from '../../auth/api';
import { useAuthStore } from '../../auth/store';
import { Card } from '../../ui/primitives';
import { Field } from './Field';

const MINIMO_SENHA = 8;

export function CriarContaScreen() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const adotar = useAuthStore((s) => s.adopt);

  const token = params.get('token') ?? undefined;
  const code = params.get('code') ?? undefined;
  const veioDeConvite = Boolean(token || code);

  const [convite, setConvite] = useState<InvitationPreview | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [enviando, setEnviando] = useState(false);

  // O convite aqui é só contexto: se não abrir, o cadastro segue igual.
  useEffect(() => {
    if (!veioDeConvite) return;
    previewInvitation(token ? { token } : { code: code ?? '' })
      .then(setConvite)
      .catch(() => setConvite(null));
  }, [token, code, veioDeConvite]);

  const senhaCurta = senha.length > 0 && senha.length < MINIMO_SENHA;
  const naoConfere = confirmacao.length > 0 && confirmacao !== senha;
  const podeEnviar =
    nome.trim().length > 0 &&
    email.trim().length > 0 &&
    nascimento.length === 10 &&
    senha.length >= MINIMO_SENHA &&
    confirmacao === senha;

  async function criar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    setEnviando(true);
    setErro(null);
    setAviso(null);

    try {
      await signUp({ name: normalizarNome(nome), email: email.trim(), password: senha });

      // A data de nascimento é da conta e entra já no cadastro (teste físico,
      // item 16): o Perfil a mostra preenchida e deixa ajustar. Se este passo
      // falhar, a conta já existe — o Perfil continua sendo o lugar de pôr.
      await fetch('/api/v1/me/birthdate', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ birthDate: nascimento }),
      }).catch(() => undefined);

      // A sessão já veio no cookie da resposta; só falta o cliente saber quem é.
      const sessao = await fetchMe();
      if (sessao) adotar(sessao.user);

      // Veio de um convite? O aceite continua — como uma SEGUNDA operação, não
      // como parte do cadastro.
      if (veioDeConvite) {
        try {
          const { tripId } = await acceptInvitationAsMe(
            token ? { token } : { code: code ?? '' },
          );
          navegar(`/viagens?entrou=${tripId}`, { replace: true });
        } catch (e) {
          // A conta EXISTE. O que falhou foi o convite — e é isso que se diz,
          // em vez de fazer parecer que o cadastro deu errado.
          navegar('/viagens', {
            replace: true,
            state: {
              aviso:
                e instanceof ApiError
                  ? `Sua conta foi criada, mas o convite não pôde ser aceito: ${e.message}`
                  : 'Sua conta foi criada, mas o convite não pôde ser aceito.',
            },
          });
        }
        return;
      }

      navegar('/viagens', { replace: true });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível criar a conta agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="auth-screen">

      {convite && (
        <div className="auth-kicker auth-kicker--brand">
          CONVITE DE {convite.tripName.toUpperCase()}
        </div>
      )}
      <h1 className="auth-title">Criar conta</h1>
      <p className="auth-subtitle">
        {convite
          ? `Crie sua conta para entrar em ${convite.tripName}.`
          : 'A conta é sua. As viagens vêm depois — você cria uma, ou entra numa por convite.'}
      </p>

      <form className="auth-form" onSubmit={criar}>
        <Field
          label="Nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          autoComplete="name"
          required
        />
        <Field
          label="E-mail"
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <Field
          label="Data de nascimento"
          type="date"
          value={nascimento}
          onChange={(e) => setNascimento(e.target.value)}
          autoComplete="bday"
          max={new Date().toISOString().slice(0, 10)}
          required
          data-testid="nascimento-cadastro"
        />
        <div className="auth-form__row">
          <Field
            label="Senha"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="new-password"
            required
            error={senhaCurta ? `Mínimo de ${MINIMO_SENHA} caracteres.` : undefined}
          />
          <Field
            label="Confirmar senha"
            type="password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            autoComplete="new-password"
            required
            error={naoConfere ? 'As senhas não coincidem.' : undefined}
          />
        </div>

        {erro && (
          <p className="auth-error" role="alert">
            {erro}
          </p>
        )}
        {aviso && (
          <p className="auth-note" role="status">
            {aviso}
          </p>
        )}

        <button
          type="submit"
          className="nv-btn nv-btn--primary nv-btn--block"
          disabled={!podeEnviar || enviando}
        >
          {enviando ? 'Criando…' : 'Criar conta'}
        </button>
      </form>

      <Link className="auth-link" to="/entrar">
        Já tenho conta
      </Link>

      {!convite && (
        <Card variant="chip">
          <p className="auth-note">
            Depois de criar a conta você pode criar uma viagem ou entrar numa
            viagem existente com um convite.
          </p>
        </Card>
      )}
    </section>
  );
}
